// src/pages/PartnerDashboard.jsx
// Route: /partner/:agentId   e.g. routechef.com/partner/ramesh.kulkarni32
//
// Access rules
//   - not signed in               -> partner landing with sign in / sign up (no data shown)
//   - signed in as a different id -> redirected to own dashboard
//   - signed in as :agentId       -> dashboard
//
// Data: built in the browser from the existing booking history intents (bookinghistory for
// flights, bus_booking_history for buses), called as the partner. See utils/agentBookings.js.
// Commission comes from each booking's `net_agent_commission` (0 when missing) and only
// counts for confirmed bookings. Add ?demo=1 to preview with sample data.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  LayoutDashboard,
  Ticket,
  IndianRupee,
  UserRound,
  Search,
  MessageSquare,
  LogOut,
  Menu,
  X,
  Bus,
  Plane,
  CheckCircle2,
  Briefcase,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import AgentAuthModal from "../components/agent/AgentAuthModal";
import {
  useAgentSession,
  clearAgentSession,
  verifyAgentSession,
  partnerDashboardPath,
} from "../utils/agentAuth";
import { loadAgentBookings, summarize, bucketOf } from "../utils/agentBookings";

const ORANGE = "#FF4E01";

/* ── formatting ──────────────────────────────────────────────────────────── */
const inr = (n) =>
  typeof n === "number" && !Number.isNaN(n)
    ? "₹" + Math.round(n).toLocaleString("en-IN")
    : "₹0";
const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};
const monthLabel = (ym) => {
  const [y, m] = String(ym).split("-").map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", { month: "short" });
};
const TURNOVER_LABELS = {
  below_10l: "Below ₹10 lakh",
  "10l_50l": "₹10 lakh to ₹50 lakh",
  "50l_1cr": "₹50 lakh to ₹1 crore",
  "1cr_5cr": "₹1 crore to ₹5 crore",
  above_5cr: "Above ₹5 crore",
};

const EMPTY_DATA = { bookings: [], ...summarize([]) };

/* Sample data for ?demo=1 only. Never shown otherwise. Uses the same shape + maths as live data. */
function demoData() {
  const now = new Date();
  const ago = (days) => new Date(now.getTime() - days * 864e5).toISOString();
  const ahead = (days) => new Date(now.getTime() + days * 864e5).toISOString().slice(0, 10);
  const mk = (id, type, status, source, destination, created, journey, pnr, pax, amount, commission) => ({
    id, type, status, bucket: bucketOf(status), source, destination, created_at: ago(created),
    journey_date: journey, pnr, pax, amount, commission,
  });
  const bookings = [
    mk("RCBB1001", "bus", "TICKET_ISSUED", "Bhubaneswar", "Allahabad", 1, ahead(17), "TS8812", 2, 1840, 92),
    mk("RCFB1002", "flight", "TICKET_ISSUED", "Mumbai", "Goa", 3, ahead(5), "6E4XQ2", 1, 6420, 180),
    mk("RCBB1003", "bus", "CANCELLED", "Pune", "Hyderabad", 7, ahead(1), "VR2231", 1, 1210, 60),
    mk("RCFB1004", "flight", "TICKET_ISSUED", "Delhi", "Bangalore", 19, ahead(-10), "AI9KL1", 2, 12880, 360),
    mk("RCFB1005", "flight", "PAYMENT_SUCCESS", "Chennai", "Delhi", 0, ahead(9), "", 1, 7310, 0),
    mk("RCBB1006", "bus", "TICKET_ISSUED", "Bangalore", "Coorg", 35, ahead(-30), "KS5520", 3, 2100, 105),
    mk("RCFB1007", "flight", "TICKET_ISSUED", "Kolkata", "Bagdogra", 64, ahead(-60), "UK77Q1", 2, 9120, 240),
    mk("RCFB1008", "flight", "TICKET_ISSUED", "Hyderabad", "Goa", 98, ahead(-90), "SG12PL", 1, 5480, 150),
    mk("RCBB1009", "bus", "TICKET_ISSUED", "Indore", "Jabalpur", 130, ahead(-125), "HN0931", 4, 3200, 160),
  ];
  return { bookings, ...summarize(bookings, now) };
}

/* ── small pieces ────────────────────────────────────────────────────────── */
const BUCKET_STYLE = {
  confirmed: ["bg-green-50 text-green-700 ring-green-600/20", "Confirmed"],
  cancelled: ["bg-slate-100 text-slate-600 ring-slate-500/20", "Cancelled"],
  failed: ["bg-red-50 text-red-700 ring-red-600/20", "Failed"],
  processing: ["bg-amber-50 text-amber-700 ring-amber-600/20", "Processing"],
};
const REFUND_LABELS = {
  REFUND_REQUESTED: "Refund requested",
  REFUND_IN_PROGRESS: "Refund in progress",
  REFUND_COMPLETED: "Refunded",
  CANCELLATION_IN_PROGRESS: "Cancelling",
};
function StatusPill({ bucket, status }) {
  const [cls, label] = BUCKET_STYLE[bucket] || BUCKET_STYLE.processing;
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${cls}`}>
      {REFUND_LABELS[status] || label}
    </span>
  );
}

function StatTile({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="m-0 text-[12.5px] font-medium text-slate-500">{label}</p>
      <p className="m-0 mt-1 text-[24px] font-bold tabular-nums tracking-tight text-slate-900">{value}</p>
      {sub && <p className="m-0 mt-0.5 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

function EmptyState({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
      <Icon size={30} className="mx-auto text-slate-300" />
      <p className="m-0 mt-3 font-semibold text-slate-800">{title}</p>
      <div className="mt-1 text-sm text-slate-500">{children}</div>
    </div>
  );
}

/* Single-series bar chart: monthly commission. Plain SVG-free divs, hover tooltip per bar. */
function CommissionChart({ monthly }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...monthly.map((m) => m.commission || 0));
  if (!monthly.length || monthly.every((m) => !m.commission)) {
    return <EmptyState icon={IndianRupee} title="No commission yet">Commission from confirmed bookings will chart here by month.</EmptyState>;
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="m-0 text-[15px] font-semibold text-slate-900">Commission by month</h3>
        <span className="text-xs text-slate-500">Last {monthly.length} months</span>
      </div>
      <div className="relative flex h-44 items-end gap-2 border-b border-slate-200 sm:gap-4" role="img" aria-label="Commission by month bar chart">
        {monthly.map((m, i) => {
          const h = m.commission > 0 ? Math.max(2, (m.commission / max) * 100) : 0;
          return (
            <div
              key={m.month}
              className="relative flex h-full flex-1 cursor-default items-end justify-center"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              tabIndex={0}
            >
              <div
                className="w-full max-w-[44px] rounded-t transition-opacity"
                style={{ height: `${h}%`, background: ORANGE, opacity: hover === null || hover === i ? 1 : 0.45 }}
              />
              {hover === i && (
                <div className="pointer-events-none absolute bottom-full z-10 mb-1 whitespace-nowrap rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs text-white shadow-lg">
                  <div className="font-semibold">{monthLabel(m.month)}</div>
                  <div className="tabular-nums">{inr(m.commission)} commission</div>
                  <div className="tabular-nums text-slate-300">{m.bookings} bookings · {inr(m.booking_value)}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex gap-2 sm:gap-4">
        {monthly.map((m) => (
          <span key={m.month} className="flex-1 text-center text-[11px] text-slate-500">{monthLabel(m.month)}</span>
        ))}
      </div>
      {/* table view for accessibility */}
      <details className="mt-3 text-sm">
        <summary className="cursor-pointer text-xs font-medium text-slate-500">Show as table</summary>
        <table className="mt-2 w-full text-left text-xs tabular-nums">
          <thead className="text-slate-500">
            <tr><th className="py-1 font-medium">Month</th><th className="font-medium">Bookings</th><th className="font-medium">Value</th><th className="font-medium">Commission</th></tr>
          </thead>
          <tbody className="text-slate-800">
            {monthly.map((m) => (
              <tr key={m.month} className="border-t border-slate-100">
                <td className="py-1">{m.month}</td><td>{m.bookings}</td><td>{inr(m.booking_value)}</td><td>{inr(m.commission)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

/* Bookings timeline grouped by booking date */
function BookingsTimeline({ bookings, filter, setFilter, limit, loading }) {
  const list = useMemo(() => {
    const f = filter === "all" ? bookings : bookings.filter((b) => b.type === filter);
    return limit ? f.slice(0, limit) : f;
  }, [bookings, filter, limit]);

  const groups = useMemo(() => {
    const g = [];
    list.forEach((b) => {
      const key = fmtDate(b.created_at);
      const last = g[g.length - 1];
      if (last && last.key === key) last.items.push(b);
      else g.push({ key, items: [b] });
    });
    return g;
  }, [list]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-[15px] font-semibold text-slate-900">{limit ? "Recent bookings" : "All bookings"}</h3>
        {setFilter && (
          <div className="flex rounded-lg bg-slate-100 p-0.5 text-xs font-semibold">
            {[["all", "All"], ["bus", "Bus"], ["flight", "Flight"]].map(([k, l]) => (
              <button
                key={k}
                type="button"
                onClick={() => setFilter(k)}
                className={`rounded-md px-3 py-1 ${filter === k ? "bg-white text-[#FF4E01] shadow-sm" : "text-slate-600"}`}
              >
                {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {!list.length && loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />
          ))}
        </div>
      ) : !list.length ? (
        <EmptyState icon={Ticket} title="No bookings yet">
          Bus and flight bookings you make while signed in will appear here.
        </EmptyState>
      ) : (
        <ol className="m-0 list-none p-0">
          {groups.map((g) => (
            <li key={g.key} className="mb-3 last:mb-0">
              <p className="m-0 mb-1.5 text-[11.5px] font-semibold uppercase tracking-wide text-slate-400">{g.key}</p>
              <ul className="m-0 list-none space-y-2 border-l-2 border-slate-100 p-0 pl-3">
                {g.items.map((b) => {
                  const Icon = b.type === "flight" ? Plane : Bus;
                  return (
                    <li key={`${b.type}-${b.id}`} className="flex items-start gap-3 rounded-lg px-2 py-2 hover:bg-slate-50">
                      <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-orange-50 text-[#FF4E01]">
                        <Icon size={16} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="truncate text-sm font-semibold text-slate-900">
                            {b.source || "–"} → {b.destination || "–"}
                          </span>
                          <StatusPill bucket={b.bucket} status={b.status} />
                        </div>
                        <p className="m-0 mt-0.5 text-xs text-slate-500">
                          {b.journey_date ? `Travel ${fmtDate(b.journey_date)}` : "Travel date –"}
                          {b.pnr ? ` · PNR ${b.pnr}` : ""}
                          {b.pax ? ` · ${b.pax} ${b.type === "bus" ? (b.pax > 1 ? "seats" : "seat") : "pax"}` : ""}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="m-0 text-sm font-semibold tabular-nums text-slate-900">{inr(b.amount)}</p>
                        <p className="m-0 text-xs tabular-nums text-slate-500">
                          {b.bucket === "confirmed"
                            ? `+${inr(b.commission)} commission`
                            : b.bucket === "processing"
                              ? "Commission on confirmation"
                              : "No commission"}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/* Height of the site's global fixed navbar (.top-nav), kept in sync on resize */
function useNavOffset() {
  const [h, setH] = useState(0);
  useLayoutEffect(() => {
    const nav = document.querySelector(".top-nav");
    if (!nav) return undefined;
    const measure = () => {
      const pos = window.getComputedStyle(nav).position;
      setH(pos === "fixed" || pos === "sticky" || pos === "absolute" ? nav.offsetHeight : 0);
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(nav);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  return h;
}

/* ── logged-out view ─────────────────────────────────────────────────────── */
function PartnerLanding() {
  const [modal, setModal] = useState(null);
  const navH = useNavOffset();
  return (
    <div className="min-h-[70vh] bg-slate-50 px-4 py-16" style={{ paddingTop: navH + 64 }}>
      <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-50 text-[#FF4E01]">
          <Briefcase size={22} />
        </span>
        <h1 className="mt-4 text-xl font-bold text-slate-900">RouteChef partner dashboard</h1>
        <p className="mt-2 text-sm text-slate-600">
          Sign in to see your bus and flight bookings and the commission you have earned.
        </p>
        <div className="mt-6 flex flex-col gap-2.5">
          <button type="button" onClick={() => setModal("signin")} className="rounded-lg bg-[#FF4E01] py-3 font-semibold text-white hover:bg-[#e84600]">
            Sign in
          </button>
          <button type="button" onClick={() => setModal("signup")} className="rounded-lg border border-slate-300 py-3 font-semibold text-slate-700 hover:bg-slate-50">
            Become a partner
          </button>
        </div>
      </div>
      <AgentAuthModal open={!!modal} initialMode={modal || "signin"} onClose={() => setModal(null)} />
    </div>
  );
}

/* ── page ────────────────────────────────────────────────────────────────── */
const NAV = [
  { key: "overview", label: "Dashboard", icon: LayoutDashboard },
  { key: "bookings", label: "Bookings", icon: Ticket },
  { key: "earnings", label: "Earnings", icon: IndianRupee },
  { key: "profile", label: "Profile", icon: UserRound },
];

export default function PartnerDashboard() {
  const { agentId } = useParams();
  const session = useAgentSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const demo = params.get("demo") === "1";
  const tab = NAV.some((n) => n.key === params.get("tab")) ? params.get("tab") : "overview";

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState({});
  const [truncated, setTruncated] = useState(false);
  const [filter, setFilter] = useState("all");
  const [showWelcome, setShowWelcome] = useState(Boolean(location.state?.justRegistered));
  const navH = useNavOffset();

  const load = useCallback(async () => {
    if (demo) {
      setData(demoData());
      setLoadErrors({});
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await loadAgentBookings();
    if (res.unauthorized) {
      // token rejected by the booking backend: confirm with agent_profile (signs out if really invalid)
      verifyAgentSession({ force: true });
    }
    setData({ bookings: res.bookings, ...summarize(res.bookings) });
    setLoadErrors(res.errors);
    setTruncated(res.truncated);
    setLoading(false);
  }, [demo]);

  useEffect(() => {
    if (!session) return;
    load();
    // re-validate the session (approval can be revoked server side)
    verifyAgentSession({ force: true });
  }, [session?.profile?.user_id, load]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showWelcome) return undefined;
    // drop router state so a refresh does not show the banner again
    window.history.replaceState({}, document.title);
    const t = setTimeout(() => setShowWelcome(false), 8000);
    return () => clearTimeout(t);
  }, [showWelcome]);

  if (!session) return <PartnerLanding />;
  if (session.profile.user_id !== String(agentId || "").toLowerCase()) {
    return <Navigate to={partnerDashboardPath(session.profile.user_id)} replace />;
  }

  const { profile } = session;
  const s = data.summary || EMPTY_DATA.summary;

  const goTab = (k) => {
    const next = new URLSearchParams(params);
    if (k === "overview") next.delete("tab");
    else next.set("tab", k);
    setParams(next, { replace: true });
    setDrawerOpen(false);
  };

  const signOut = () => {
    clearAgentSession();
    navigate("/", { replace: true });
  };

  const Drawer = (
    <nav className="flex h-full flex-col" aria-label="Partner navigation">
      <div className="border-b border-slate-100 px-4 py-4">
        <p className="m-0 truncate text-[15px] font-bold text-slate-900">{profile.business_name || profile.name}</p>
        <p className="m-0 truncate text-xs text-slate-500">{profile.user_id}</p>
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-3">
        {NAV.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => goTab(key)}
            className={`mb-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium ${
              tab === key ? "bg-orange-50 text-[#FF4E01]" : "text-slate-700 hover:bg-slate-50"
            }`}
            aria-current={tab === key ? "page" : undefined}
          >
            <Icon size={18} /> {label}
          </button>
        ))}
        <p className="m-0 mb-1 mt-5 px-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Book for customers</p>
        <Link to="/" onClick={() => setDrawerOpen(false)} className="mb-0.5 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
          <Search size={18} /> Route search (form)
        </Link>
        <Link to="/chat" onClick={() => setDrawerOpen(false)} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
          <MessageSquare size={18} /> Route search (chat)
        </Link>
      </div>
      <div className="border-t border-slate-100 p-2">
        <button type="button" onClick={signOut} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50" style={{ paddingTop: navH }}>
      {/* mobile top bar */}
      <div style={{ top: navH }} className="sticky z-30 flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
        <button type="button" onClick={() => setDrawerOpen(true)} className="rounded-lg p-1.5 text-slate-700 hover:bg-slate-100" aria-label="Open menu">
          <Menu size={22} />
        </button>
        <span className="font-semibold text-slate-900">{NAV.find((n) => n.key === tab)?.label}</span>
      </div>

      {/* mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setDrawerOpen(false)} />
          <div className="absolute bottom-0 left-0 w-[272px] max-w-[85vw] bg-white shadow-xl" style={{ top: navH }}>
            <button type="button" onClick={() => setDrawerOpen(false)} className="absolute right-2 top-3 rounded-full p-1.5 text-slate-400 hover:bg-slate-100" aria-label="Close menu">
              <X size={20} />
            </button>
            {Drawer}
          </div>
        </div>
      )}

      <div className="mx-auto flex max-w-[1280px]">
        {/* desktop sidebar */}
        <aside
          className="sticky hidden w-[248px] flex-shrink-0 border-r border-slate-200 bg-white lg:block"
          style={{ top: navH, height: `calc(100vh - ${navH}px)` }}
        >
          {Drawer}
        </aside>

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          {showWelcome && (
            <div className="mb-5 flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
              <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0 text-green-600" />
              <div className="text-sm text-green-900">
                <p className="m-0 font-semibold">Your partner login is created successfully</p>
                <p className="m-0">Sign in next time with <span className="font-semibold">{profile.user_id}</span> or {profile.email}.</p>
              </div>
            </div>
          )}

          {demo && (
            <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">Demo mode: sample data, not your real bookings.</p>
          )}

          <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="m-0 text-[22px] font-bold text-slate-900">
                {tab === "overview" ? `Hi ${profile.name?.split(" ")[0] || "there"}` : NAV.find((n) => n.key === tab)?.label}
              </h1>
              {tab === "overview" && <p className="m-0 mt-0.5 text-sm text-slate-500">Your bookings and earnings at a glance</p>}
            </div>
            {tab !== "profile" && (
              <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
            )}
          </div>

          {!demo && (loadErrors.flight || loadErrors.bus || truncated) && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <AlertTriangle size={15} className="mt-px flex-shrink-0" />
              <div>
                {loadErrors.flight && <p className="m-0">Flight bookings could not be loaded, so totals below exclude them. Try Refresh.</p>}
                {loadErrors.bus && <p className="m-0">Bus bookings could not be loaded, so totals below exclude them. Try Refresh.</p>}
                {truncated && <p className="m-0">Showing your latest 1,000 bookings per type. Older ones are not included in totals.</p>}
              </div>
            </div>
          )}

          {/* OVERVIEW */}
          {tab === "overview" && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <StatTile label="Confirmed bookings" value={loading ? "…" : s.total_bookings} sub={`${s.bus_bookings} bus · ${s.flight_bookings} flight`} />
                <StatTile label="Booking value" value={loading ? "…" : inr(s.total_booking_value)} sub="Confirmed fares" />
                <StatTile label="Commission earned" value={loading ? "…" : inr(s.commission_earned)} sub={`${inr(s.commission_this_month)} this month`} />
                <StatTile label="Upcoming trips" value={loading ? "…" : s.upcoming_trips} sub={s.processing ? `${s.processing} processing` : "Confirmed, not yet travelled"} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Link to="/" className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-[#FF4E01]">
                  <Search size={20} className="text-[#FF4E01]" />
                  <div><p className="m-0 font-semibold text-slate-900">Search routes</p><p className="m-0 text-xs text-slate-500">Form search for trains, flights, buses</p></div>
                </Link>
                <Link to="/chat" className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:border-[#FF4E01]">
                  <MessageSquare size={20} className="text-[#FF4E01]" />
                  <div><p className="m-0 font-semibold text-slate-900">Chat search</p><p className="m-0 text-xs text-slate-500">Describe the trip in any language</p></div>
                </Link>
              </div>
              <BookingsTimeline bookings={data.bookings || []} filter="all" limit={5} loading={loading} />
              {(data.bookings || []).length > 5 && (
                <button type="button" onClick={() => goTab("bookings")} className="text-sm font-semibold text-[#FF4E01] hover:underline">View all bookings</button>
              )}
            </div>
          )}

          {/* BOOKINGS */}
          {tab === "bookings" && <BookingsTimeline bookings={data.bookings || []} filter={filter} setFilter={setFilter} loading={loading} />}

          {/* EARNINGS */}
          {tab === "earnings" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <StatTile label="Commission earned" value={inr(s.commission_earned)} sub="All confirmed bookings" />
                <StatTile label="This month" value={inr(s.commission_this_month)} />
                <StatTile label="Average per booking" value={inr(s.avg_commission)} />
              </div>
              <CommissionChart monthly={data.monthly || []} />
            </div>
          )}

          {/* PROFILE */}
          {tab === "profile" && (
            <div className="max-w-xl rounded-xl border border-slate-200 bg-white">
              {[
                ["User ID", profile.user_id],
                ["Name", profile.name],
                ["Email", profile.email],
                ["Mobile", profile.phone],
                ["Registered business", profile.is_registered_business ? "Yes" : "No"],
                ...(profile.is_registered_business ? [["Business name", profile.business_name]] : []),
                ["Annual turnover", TURNOVER_LABELS[profile.annual_turnover] || "Not shared"],
                ["Partner since", fmtDate(profile.created_at)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 px-4 py-3 text-sm last:border-b-0">
                  <span className="text-slate-500">{k}</span>
                  <span className="break-all text-right font-medium text-slate-900">{v || "–"}</span>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
